import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ru from './locales/ru.json';
import en from './locales/en.json';

/**
 * Internationalisation (react-i18next).
 *
 * Russian is the project's first language (the cispracs.ru community); English
 * is the alternative. Resources are bundled (not lazy-loaded) so the very first
 * paint is already translated — no Suspense flash, no FOUC. Strings live in
 * `locales/<lang>.json` as nested keys grouped by surface (landing, settings,
 * …). Components read them with `useTranslation()` / `t('landing.title')`, or
 * `<Trans>` when a string embeds markup.
 *
 * Language resolution (detector): the user's saved choice (localStorage key
 * `bunker.lang`) wins; otherwise the browser language if it's ru/en; otherwise
 * ru. Changing it in Settings calls `i18n.changeLanguage(id)`, which persists
 * back to localStorage via the detector cache.
 */
export const SUPPORTED_LANGUAGES = [
  { id: 'ru', label: 'Русский' },
  { id: 'en', label: 'English' },
] as const;

export type LangId = (typeof SUPPORTED_LANGUAGES)[number]['id'];

export const LANG_STORAGE_KEY = 'bunker.lang';

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ru: { translation: ru },
      en: { translation: en },
    },
    fallbackLng: 'ru',
    supportedLngs: ['ru', 'en'],
    // Map regional tags (e.g. 'en-US', 'ru-RU') onto the base language.
    nonExplicitSupportedLngs: true,
    interpolation: {
      // React already escapes interpolated values — double-escaping would
      // mangle them.
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ['localStorage'],
    },
    react: {
      // Resources are bundled synchronously, so we don't need Suspense.
      useSuspense: false,
    },
  });

export default i18n;
